import { useState } from 'react';

import type { MeterDetail } from '@rheora/enrollment-contract';

import { useTranslation } from '@fluxora/i18n';
import { Badge, Button, Card, CardBody } from '@fluxora/ui';

import { meterSummaryFields } from '../services/meterDetail.service';
import { enrollmentTypeLabelKey } from '../services/serviceability.service';
import { MeterDetailsDialog } from './MeterDetailsDialog';
import { MeterFieldList } from './MeterFieldList';

/**
 * The meter-summary card the shopper sees immediately after either the ZIP or
 * the ESI ID on-ramp resolves their meter (§3 of the epic). Both on-ramps
 * converge on one `MeterDetail`, so this ONE card serves both.
 *
 * ── FIELDS ─────────────────────────────────────────────────────────────────
 * ZIP · address · TDU · usage · customer type — plus an enrollment-type badge
 * (Switch vs Move-in). Rows are derived in `meterSummaryFields` (services/) so
 * this component stays render-only. All values are REAL (dummy-data stub today):
 * usage + customer type come straight from the resolved meter.
 *
 * ── SEE ALL DETAILS ────────────────────────────────────────────────────────
 * An icon-only "see all meter details" button opens `MeterDetailsDialog` with
 * the full field set (research §2). Card + dialog are shared across both on-ramps.
 *
 * The "Shop plans" CTA is NOT on this card — it lives on `ResolvedMeterPanel`
 * BELOW the consumption section, so it can be gated on a valid usage choice.
 *
 * ── AC3 POSTURE ────────────────────────────────────────────────────────────
 * The ESI ID (`meterDetail.esiid`) is display-only; nothing here egresses it as a
 * key. Downstream reads the meter + candidateId from `FunnelProvider`.
 */
export interface MeterSummaryCardProps {
  /** The resolved meter (from either on-ramp). */
  readonly meterDetail: MeterDetail;
  /**
   * Called when the shopper wants to try a different address/ESI ID. The parent
   * typically forwards this to `clearResolved()` from `FunnelProvider`. Absent
   * when the parent does not want a "start over" affordance.
   */
  readonly onStartOver?: () => void;
}

export function MeterSummaryCard({ meterDetail, onStartOver }: MeterSummaryCardProps): JSX.Element {
  const { t } = useTranslation();
  const [detailsOpen, setDetailsOpen] = useState(false);

  return (
    /* Same 1.5px pastel-tint ring as the contact card — the funnel's
       "you are here" marker, without the hard indigo outline. */
    <div className="rounded-[calc(var(--r-lg)+1.5px)] bg-[image:var(--brand-gradient-tint)] p-[1.5px] shadow-card">
    <Card variant="glass" padding="md" className="!rounded-[var(--r-lg)]">
      <CardBody className="flex flex-col gap-3 p-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-primary">
              {t('shop.meter.summary.eyebrow')}
            </span>
            <h2 className="font-display text-xl font-bold tracking-tight text-foreground">
              {t('shop.meter.summary.title')}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={meterDetail.enrollmentType === 'SWITCH' ? 'primary' : 'success'}>
              {t(enrollmentTypeLabelKey(meterDetail.enrollmentType))}
            </Badge>
            {/* A NAMED control, not a bare ⓘ — "what does this circle do"
                should never be a question on a confirmation card. */}
            <Button
              variant="secondary"
              size="sm"
              shape="pill"
              onClick={() => setDetailsOpen(true)}
              title={t('shop.meter.summary.seeAll.title')}
            >
              {t('shop.meter.summary.seeAll.label')}
            </Button>
          </div>
        </div>

        <MeterFieldList fields={meterSummaryFields(meterDetail)} columns={2} />

        {onStartOver !== undefined ? (
          <div className="flex sm:justify-end">
            <Button variant="secondary" shape="pill" onClick={onStartOver}>
              {t('shop.meter.summary.cta.startOver')}
            </Button>
          </div>
        ) : null}
      </CardBody>

      <MeterDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        meterDetail={meterDetail}
      />
    </Card>
    </div>
  );
}
